import { normalizeFeaturedVehicleSelection } from "./featuredVehicleSelection";
import { normalizeInventoryStatusKeys } from "./inventoryStatusSelection";

export const WEB_MAKER_SITE_SCHEMA_VERSION = 5;
export const WEB_MAKER_SUPPORTED_SCHEMA_VERSIONS = Object.freeze([0, 1, 2, 3, 4, 5]);

const cloneJson = (value) => {
  if (!value || typeof value !== "object") return {};
  return JSON.parse(JSON.stringify(value));
};

const hasOwn = (value, key) =>
  Boolean(value && Object.prototype.hasOwnProperty.call(value, key));

export const getWebMakerSiteSchemaVersion = (config) => {
  const version = Number(config?.schemaVersion);
  return Number.isInteger(version) && version >= 1 ? version : 0;
};

const migrateVersion0To1 = (config) => ({
  ...config,
  schemaVersion: 1,
  ...(!hasOwn(config, "structuredLayouts") ? { structuredLayouts: [] } : {}),
});

const migrateVersion1To2 = (config) => {
  const branding =
    config.branding && typeof config.branding === "object" && !Array.isArray(config.branding)
      ? config.branding
      : null;
  return {
    ...config,
    schemaVersion: 2,
    ...(!hasOwn(config, "branding")
      ? { branding: { stylePresets: {} } }
      : branding && !hasOwn(branding, "stylePresets")
        ? { branding: { ...branding, stylePresets: {} } }
        : {}),
  };
};

const migrateVersion2To3 = (config) => ({
  ...config,
  schemaVersion: 3,
  ...(!hasOwn(config, "customOverlayGroups") ? { customOverlayGroups: [] } : {}),
});

const mapFeaturedSectionData = (config, patchData) =>
  Array.isArray(config.sections)
    ? {
        sections: config.sections.map((section) => {
          if (section?.type !== "featured_vehicles") return section;
          const data =
            section.data && typeof section.data === "object" && !Array.isArray(section.data)
              ? section.data
              : {};
          return {
            ...section,
            data: {
              ...data,
              ...patchData(data),
            },
          };
        }),
      }
    : {};

const migrateVersion3To4 = (config) => ({
  ...config,
  schemaVersion: 4,
  ...mapFeaturedSectionData(config, (data) => ({
    vehicleSelection: normalizeFeaturedVehicleSelection(
      data.vehicleSelection,
      data
    ),
  })),
});

const migrateVersion4To5 = (config) => ({
  ...config,
  schemaVersion: 5,
  ...mapFeaturedSectionData(config, (data) => ({
    inventoryStatusKeys: normalizeInventoryStatusKeys(
      data.inventoryStatusKeys
    ),
  })),
});

export const WEB_MAKER_SITE_MIGRATIONS = Object.freeze({
  0: migrateVersion0To1,
  1: migrateVersion1To2,
  2: migrateVersion2To3,
  3: migrateVersion3To4,
  4: migrateVersion4To5,
});

/**
 * Immutable, additive-only forward migration for persisted Web Maker config.
 * Future versions are preserved verbatim and reported instead of downgraded.
 */
export const migrateWebMakerSiteConfig = (rawConfig) => {
  let config = cloneJson(rawConfig);
  const sourceVersion = getWebMakerSiteSchemaVersion(config);
  const applied = [];

  if (sourceVersion > WEB_MAKER_SITE_SCHEMA_VERSION) {
    return {
      config,
      sourceVersion,
      targetVersion: sourceVersion,
      applied,
      unsupportedFutureVersion: true,
    };
  }

  let version = sourceVersion;
  while (version < WEB_MAKER_SITE_SCHEMA_VERSION) {
    const migrate = WEB_MAKER_SITE_MIGRATIONS[version];
    if (typeof migrate !== "function") {
      return {
        config,
        sourceVersion,
        targetVersion: version,
        applied,
        unsupportedVersion: true,
      };
    }
    const next = migrate(config);
    applied.push({ from: version, to: version + 1 });
    config = next;
    version += 1;
  }

  return {
    config,
    sourceVersion,
    targetVersion: version,
    applied,
    unsupportedFutureVersion: false,
  };
};

export default migrateWebMakerSiteConfig;
